import {
  type GetTicketsQuery,
  type TicketStatus,
  type TicketType,
  type TicketFilters,
  type TicketSortInput,
} from "#graphql/supergraphTypes";

export type AGGridCellRenderParams = {
  value: string | number;
  rowIndex: number;
  [key: string]: unknown;
};

export type TypedAGGridCellRenderParams<T> = {
  value: string | number;
  rowIndex: number;
  [key: string]: unknown;
  data: T;
};

export type TicketAssignee = {
  id: string;
  displayName: string;
  email?: string | null;
};

export type TicketClient = {
  id: string;
  name: string;
  type?: string | null;
  contactEmail?: string | null;
};

export type TicketManager = {
  id: string;
  displayName: string;
  email?: string | null;
  department?: string | null;
};

export type TicketProgress = {
  completedTasks: number;
  totalTasks: number;
  percentage: number;
};

export type TicketRowItem = {
  id: string;
  key: string;
  description: string;
  ticketType: TicketType;
  workType: string;
  status: TicketStatus;
  statusCategoryName?: string | null;
  customers: string[];
  entityName: string[];
  createdAt: string;
  updated?: string | null;
  jiraLink: string;
  isPrimary?: string | null;
  isReviewer?: string | null;
  faPreparer?: string | null;
  faReviewer?: string | null;
  requestedBy?: string | null;
  assignedTo?: TicketAssignee | null;
  client?: TicketClient | null;
  manager?: TicketManager | null;
  managerOfAssignedTo?: { displayName: string; email: string | null } | null;
  progress: TicketProgress;
  pastDue: number;
  timeElapsed: number;
  dueDate?: string | null;
};

export type ExtendedTicketFilters = TicketFilters & {
  workTypes?: string[];
};

export type TicketsTableProps = {
  initialFilters?: ExtendedTicketFilters;
  onFilterChanged?: (event: { api: { getFilterModel: () => Record<string, unknown> } }) => void;
  onSavedViewFilterApplied?: (filterModel: Record<string, unknown>) => void;
  onSortsChange?: (sorts: TicketSortInput | null) => void;
  onClearFilters?: () => void;
  excludeAllCompleted?: boolean;
  clearDashboardFilters?: boolean;
  /** Override the initially-active workflow tab key. Defaults to ASSIGNED_TO_ME when omitted. */
  defaultActiveTabKey?: string;
  dashboardFilterModel?: Record<string, unknown> | null;
};

export type TicketsResponse = NonNullable<GetTicketsQuery["tickets"]>;
export type TicketsData = NonNullable<TicketsResponse["tickets"]>;
export type FieldMetadata = NonNullable<TicketsResponse["fieldMetadata"]>;

export type TextFilter = {
  filterType: "text";
  type: string;
  filter: string;
};

export type SetFilter = {
  filterType: "set";
  values: string[];
};

export const isGridSetFilterTypeGuard = (filter: unknown): filter is SetFilter => {
  return (
    typeof filter === "object" &&
    filter !== null &&
    "filterType" in filter &&
    filter.filterType === "set" &&
    "values" in filter &&
    Array.isArray(filter.values) &&
    filter.values.every((v): v is string => typeof v === "string")
  );
};

export type BooleanFilter = {
  filterType: "boolean";
  value: boolean;
};

export type DateFilter = {
  filterType: "date";
  type: string;
  dateFrom?: string | Date;
  dateTo?: string | Date;
};

export type FilterOptionItem = {
  displayName: string;
  value: string;
};

export type FilterOptionsWithMapping = {
  displayValues: string[];
  displayToValueMap: Record<string, string>;
};

export const isFilterOptionsWithMapping = (
  value: string[] | FilterOptionsWithMapping | undefined,
): value is FilterOptionsWithMapping => {
  return (
    value !== undefined &&
    typeof value === "object" &&
    !Array.isArray(value) &&
    "displayValues" in value &&
    "displayToValueMap" in value
  );
};

export type FilterOptionsMap = Record<string, string[] | FilterOptionsWithMapping>;

export type FieldMetadataMap = Map<string, string>;
